import { Callout, SegmentedControl } from '@radix-ui/themes';
import { CircleDashed, Clock } from 'lucide-react';
import { useState, use } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { SortPreferenceContext } from '../../SortPreferenceContext';
import { track } from '../../analytics/track';
import { AnalyticsEvents } from '../../analytics/events';
import Title from '../elements/Title';
import LocaleContext from '../hooks/LocaleContext';
import useKanban, { type KanbanLeads } from '../hooks/useKanban';
import { HubertTooltip } from '../HubertTooltip';
import JobHeader from '../jobHeader/JobHeader';
import Column from './Column';

const KanbanBoard = () => {
  const { t } = useTranslation();
  const { userSettings } = use(LocaleContext);

  const { id } = useParams<{ id: string }>();
  const [columnOrder, setColumnOrder] = useState(['newApplicants', 'hubertLeads', 'acceptedLeads', 'notHired']);
  const columnTitles = [
    { id: 'newApplicants', title: t('kanban.columns.newApplicants'), skip: 0 },
    { id: 'hubertLeads', title: t('kanban.columns.hubertLeads'), skip: 0 },
    { id: 'acceptedLeads', title: t('kanban.columns.acceptedLeads'), skip: 0 },
    { id: 'hired', title: t('kanban.columns.hired'), skip: 0 },
    { id: 'notHired', title: t('kanban.columns.notHired'), skip: 0 },
  ];
  const [sort, setSort] = use(SortPreferenceContext)!;

  const { job, hubertLeads, acceptedLeads, hired, notHired, newApplicants, error, getMore, disabled, loading } =
    useKanban({ jobId: id, initLimit: 20, showPassed: true, sort });
  const navigate = useNavigate();

  const data: KanbanLeads = {
    hubertLeads,
    acceptedLeads,
    hired,
    notHired,
    newApplicants,
  };

  const toggleHired = () => {
    const temp = [...columnOrder];
    const isHired = temp[temp.length - 1] === 'hired';
    const updateHired = isHired ? 'notHired' : 'hired';
    temp[temp.length - 1] = updateHired;
    setColumnOrder(temp);
  };

  const selectCallback = (e: { jobId?: string; applicationId: string }) => {
    track(AnalyticsEvents.CANDIDATE_OPENED, { from: 'board' });
    if (userSettings.settings?.anonymized === true) {
      navigate(`/job/${e.jobId}`);
    } else {
      navigate(`/job/${e.jobId}/${e.applicationId}`);
    }
  };

  return (
    <div className="kanban">
      <Title title={job.title ? job.title : t('kanban.titleFallback')} />
      <JobHeader
        jobId={id}
        extra={
          <SegmentedControl.Root
            value={sort}
            size="3"
            onValueChange={(value) => {
              track(AnalyticsEvents.CANDIDATES_SORTED, { sort: value });
              setSort(value);
            }}
          >
            <SegmentedControl.Item value="score">
              <HubertTooltip tooltip={t('kanban.sort.score')}>
                <span className="score-pill-md dark-grey">1</span>
              </HubertTooltip>
            </SegmentedControl.Item>
            <SegmentedControl.Item value="createdAt">
              <HubertTooltip tooltip={t('kanban.sort.invitationDate')}>
                <Clock />
              </HubertTooltip>
            </SegmentedControl.Item>
            <SegmentedControl.Item value="lastActivityAt">
              <HubertTooltip tooltip={t('kanban.sort.latestActivity')}>
                <CircleDashed />
              </HubertTooltip>
            </SegmentedControl.Item>
          </SegmentedControl.Root>
        }
      />
      {error && (
        <Callout.Root color="red">
          <Callout.Text className="mb-0">{error.message}</Callout.Text>
        </Callout.Root>
      )}

      {!error && (
        <div className="filler flex">
          {columnOrder.map((col) => {
            return (
              <Column
                key={`col-${col}`}
                column={columnTitles.find((c) => c.id === col) ?? { id: '', title: '' }}
                applicants={data[col]}
                threshold={job ? job.threshold : 0}
                selectCallback={selectCallback}
                toggleColumnCallback={toggleHired}
                jobId={id}
                moreCallback={() => getMore(col)}
                disabledMore={disabled[col]}
                loading={loading}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default KanbanBoard;
